import React, { useEffect, useMemo, useState } from "react";
import styles from "./Specifications.module.scss";
import {
  Heading1,
  Body1,
  Heading2,
} from "../../../../components/value-cssmodules/TextComponent";
import SocialMediaIcon from "../../../../components/Button/SocialMediaIcon";
import dayjs from "dayjs";
import { PatternData, ProfileData } from "../../../../types/siteTypes";
import { FormattedMessage, useIntl } from "react-intl";
import CartButton from "./CartButton";
import { addToList, removeFromList } from "../../../../actions/cartActions";
import { useDispatch, useSelector } from "react-redux";
import { favouritesSelector, authSelector, cartSelector } from "../../../../utils/selectors";
import {
  formatEur,
  getPatternPricingPresentation,
} from "../../../../utils/patternPricing";
import {
  formatPatternSizeLabel,
  getOrderedPatternSizes,
  resolveCartSelectedSize,
} from "../../../../utils/patternSize";

type Props = {
  pattern: PatternData;
  profile: ProfileData;
};

const Specifications: React.FC<Props> = ({
  profile,
  pattern: {
    name,
    price,
    discount,
    omnibus,
    displayPricing,
    displayOmnibus,
    description,
    sizes,
    dateUpdated,
    _id,
    user,
  },
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const favourites = useSelector((state: any) => favouritesSelector(state));
  const cart = useSelector((state: any) => cartSelector(state));
  const { user: currentUser } = useSelector((state: any) => authSelector(state));
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);

  const inFavourites =
    favourites.filter((item: any) => item.id === _id).length > 0;
  const orderedSizes = useMemo(
    () => getOrderedPatternSizes(Array.isArray(sizes) ? sizes : []),
    [sizes]
  );
  const {
    showPromotionInfo,
    promotionBasePrice,
    currentEffectivePrice,
    lowestEffectivePrice,
    showOmnibusInfo,
  } = getPatternPricingPresentation({
    price,
    discount,
    omnibus,
    displayPricing,
    displayOmnibus,
  });
  const selectedCartSizesForPattern = useMemo(
    () =>
      cart
        .filter((item: any) => item.id === _id)
        .map((item: any) => resolveCartSelectedSize(item))
        .filter(Boolean),
    [cart, _id]
  );

  const isOwner = currentUser && user === currentUser._id;

  useEffect(() => {
    setSelectedSizes((previous) => {
      const next = previous.filter(
        (size) =>
          orderedSizes.includes(size) && !selectedCartSizesForPattern.includes(size)
      );
      const isSame =
        next.length === previous.length &&
        next.every((size, index) => size === previous[index]);

      return isSame ? previous : next;
    });
  }, [orderedSizes, selectedCartSizesForPattern]);

  const toggleSizeSelection = (size: string) => {
    if (selectedCartSizesForPattern.includes(size)) {
      return;
    }

    setSelectedSizes((previous) =>
      previous.includes(size)
        ? previous.filter((value) => value !== size)
        : [...previous, size]
    );
  };

  return (
    <div className={styles.self}>
      <Heading1>{name} </Heading1>
      <Body1>{description}</Body1>
      <div className={styles.priceWrap}>
        {showPromotionInfo ? (
          <>
            <div className={styles.priceRow}>
              <span className={styles.oldPrice}>{formatEur(promotionBasePrice)}</span>
              <span className={styles.newPrice}>{formatEur(currentEffectivePrice)}</span>
            </div>
            {showOmnibusInfo && lowestEffectivePrice !== null && (
              <Body1 className={styles.omnibusLine}>
                <FormattedMessage
                  id="cart.omnibusInfo"
                  defaultMessage="Lowest price in 30 days before discount: {price}"
                  values={{ price: formatEur(lowestEffectivePrice) }}
                />
              </Body1>
            )}
          </>
        ) : (
          <Heading2>{formatEur(currentEffectivePrice)}</Heading2>
        )}
      </div>
      {orderedSizes.length > 0 && (
        <div className={styles.sizes}>
          {orderedSizes.map((size) => (
            <button
              key={size}
              type="button"
              disabled={selectedCartSizesForPattern.includes(size)}
              className={`${styles.sizeButton} ${
                selectedSizes.includes(size) ? styles.sizeButtonActive : ""
              } ${
                selectedCartSizesForPattern.includes(size)
                  ? styles.sizeButtonUnavailable
                  : ""
              }`.trim()}
              onClick={() => toggleSizeSelection(size)}
              aria-pressed={selectedSizes.includes(size)}
            >
              {formatPatternSizeLabel(size)}
            </button>
          ))}
        </div>
      )}

      {!isOwner && (
        <CartButton
          id={_id}
          sizes={Array.isArray(sizes) ? sizes : []}
          selectedSizes={selectedSizes}
          onSelectedSizesChange={setSelectedSizes}
        />
      )}

      {profile && dateUpdated && (
        <Body1>
          <FormattedMessage
            id={"site.specification.lastUpdate"}
            defaultMessage={"Last update"}
          />
          : {dayjs(dateUpdated).format("DD-MM-YYYY")}
        </Body1>
      )}
      {!isOwner && (
        <SocialMediaIcon
          className={`${styles.socialIcon} ${
            inFavourites ? styles.socialIconActive : ""
          }`.trim()}
          icon={inFavourites ? "heart" : "heart outline"}
          inverted={false}
          content={
            inFavourites
              ? intl.formatMessage({
                id: "cart.tableTitlefavourites.removeFromList",
              })
              : intl.formatMessage({ id: "site.addToFavourites" })
          }
          onClick={() => {
            if (inFavourites) {
              dispatch(removeFromList(_id, "favourites"));
            } else {
              dispatch(addToList(_id, "favourites"));
            }
          }}
        />
      )}
    </div>
  );
};

export default Specifications;
